import { useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, AlertCircle, CheckCircle, X } from "lucide-react";
import { useDailyWorkBulkUpload } from "../bulk-upload.hooks";

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
}

const TSV_TEMPLATE_HEADER = "property	service	subs	payment	date	max_hours";
const TSV_TEMPLATE_ROWS = [
  "The Kendrik	Cleaning	Dream Renovations	23	2026/03/16	8",
  "	HVAC	Blanca	24	2026/03/16	8",
  "	Landscaping		25	2026/03/16	8",
  "Riverside Apartments	Cleaning	Dream Renovations	22	2026/03/17	8",
  "	HVAC	Blanca	26	2026/03/17	8",
  "Sunset Towers	Cleaning		28	2026/03/18	10",
  "	Landscaping		20	2026/03/18	6",
];

export default function BulkUploadModal({ isOpen, onClose, onUploadComplete }: BulkUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    validateFile,
    upload,
    reset,
    isValidating,
    isUploading,
    uploadProgress,
    validationState,
    uploadResult,
    canUpload,
  } = useDailyWorkBulkUpload();

  const handleFileChange = useCallback(
    (selectedFile: File | null) => {
      if (!selectedFile) {
        setFile(null);
        reset();
        return;
      }
      if (!selectedFile.name.toLowerCase().endsWith(".tsv") && !selectedFile.name.toLowerCase().endsWith(".txt")) {
        return;
      }
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        validateFile(content);
      };
      reader.readAsText(selectedFile);
    },
    [validateFile, reset]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) handleFileChange(files[0]);
    },
    [handleFileChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleUpload = async () => {
    if (!canUpload) return;
    const result = await upload();
    if (result?.success) {
      onUploadComplete?.();
      onClose();
    }
  };

  const handleClose = () => {
    setFile(null);
    reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const downloadTemplate = () => {
    const template = [TSV_TEMPLATE_HEADER, ...TSV_TEMPLATE_ROWS].join("\n");
    const blob = new Blob([template], { type: "text/tab-separated-values" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "daily_work_bulk_template.tsv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Upload Daily Work</DialogTitle>
          <DialogDescription>
            Upload daily work from a TSV file. Use <strong>lowercase</strong> headers: property, service, subs,
            payment, date, max_hours. Tasks are taken from daily work task templates per service. Empty property means
            same as row above.{" "}
            <Button variant="link" className="p-0 h-auto" onClick={downloadTemplate}>
              Download template
            </Button>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="daily-work-file-upload">Select TSV File</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center space-y-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  <span className="font-medium">Click to upload</span> or drag and drop
                </span>
                <span className="text-xs text-muted-foreground">TSV files only (Max 10MB)</span>
              </div>
              <Input
                ref={fileInputRef}
                id="daily-work-file-upload"
                type="file"
                accept=".tsv,.txt"
                className="hidden"
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          {file && (
            <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
              <FileText className="h-4 w-4" />
              <span className="text-sm font-medium">{file.name}</span>
              <span className="text-sm text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
              <Button variant="ghost" size="sm" className="ml-auto h-6 w-6 p-0" onClick={() => handleFileChange(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {isValidating && (
            <Alert>
              <AlertDescription>Validating file…</AlertDescription>
            </Alert>
          )}

          {validationState && !isValidating && (
            <div className="space-y-2">
              {validationState.errors.length === 0 ? (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    File is valid. Found {validationState.rowCount} row(s). You can upload.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <div>
                        {validationState.errors.length} error(s) — fix them before uploading:
                      </div>
                      <ul className="list-disc list-inside text-sm space-y-1 max-h-40 overflow-y-auto">
                        {validationState.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Uploading…</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
            </div>
          )}

          {uploadResult && (
            <Alert
              className={
                uploadResult.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
              }
            >
              {uploadResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription
                className={uploadResult.success ? "text-green-800" : "text-red-800"}
              >
                {uploadResult.message}
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <ul className="list-disc list-inside text-sm mt-2">
                    {uploadResult.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!canUpload || isUploading}>
            {isUploading ? "Uploading…" : "Upload Daily Work"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
